import type { ExpenseCategory, OverviewBlob } from './AccountingWidgets';

/**
 * Derives the Accounting Overview / Home financial figures from the transaction ledger at runtime,
 * so the ledger is the single source of truth. Raise a profile's revenue (add/enlarge invoices in
 * its content blob) and Income, Net Income, Cash In, the income chart, and the Transactions tab all
 * move together — no hand-maintained rollup to keep in sync.
 */

interface LedgerTxn {
    amount: string;
    date: string; // ISO, e.g. "2026-07-08T00:00:00Z"
    gl_account: { id?: string; name?: string } | null;
}

export interface BookkeepingSummary {
    ytdIncome: number;
    ytdExpenses: number;
    mtdIncome: number;
    mtdExpenses: number;
    overview: OverviewBlob;
}

const CATEGORY_COLORS: ExpenseCategory['color'][] = ['primary', 'warning', 'success', 'info', 'error'];
// Owner distributions are equity, not an operating expense — they still count in cash-out.
const EXCLUDED_EXPENSE_GL = new Set(['gla_distribution']);

export function deriveBookkeepingSummary(transactions: LedgerTxn[]): BookkeepingSummary {
    // The ledger is re-dated to end this month (demo/seedDates.ts), so "year" is the current one.
    const yearPrefix = `${new Date().getFullYear()}-`;
    let ytdIncome = 0;
    let ytdExpenses = 0;
    const monthlyIncome = new Array<number>(12).fill(0);
    const byCategory = new Map<string, number>();
    const byMonth = new Map<string, { inflow: number; outflow: number }>();

    transactions.forEach((t) => {
        const amount = Number(t.amount);
        if (Number.isNaN(amount) || !t.date.startsWith(yearPrefix)) {
            return;
        }
        const ym = t.date.slice(0, 7);
        const month = byMonth.get(ym) ?? { inflow: 0, outflow: 0 };

        if (amount > 0) {
            ytdIncome += amount;
            month.inflow += amount;
            monthlyIncome[Number(t.date.slice(5, 7)) - 1] += amount;
        } else if (amount < 0) {
            const spend = -amount;
            ytdExpenses += spend;
            month.outflow += spend;
            const glId = t.gl_account?.id;
            if (!glId || !EXCLUDED_EXPENSE_GL.has(glId)) {
                const name = t.gl_account?.name ?? 'Uncategorized';
                byCategory.set(name, (byCategory.get(name) ?? 0) + spend);
            }
        }
        byMonth.set(ym, month);
    });

    // "This month" = the most recent month present in the ledger.
    const latestMonth = [...byMonth.keys()].sort().pop();
    const mtd = (latestMonth && byMonth.get(latestMonth)) || { inflow: 0, outflow: 0 };

    const expenseCategories: ExpenseCategory[] = [...byCategory.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, amount], i) => ({ name, amount: Math.round(amount), color: CATEGORY_COLORS[i] }));

    return {
        ytdIncome: Math.round(ytdIncome),
        ytdExpenses: Math.round(ytdExpenses),
        mtdIncome: Math.round(mtd.inflow),
        mtdExpenses: Math.round(mtd.outflow),
        overview: {
            cash_in: Math.round(ytdIncome),
            cash_out: Math.round(ytdExpenses),
            change_in_cash: Math.round(ytdIncome - ytdExpenses),
            expense_categories: expenseCategories,
            monthly_income: monthlyIncome.map((v) => Math.round(v)),
        },
    };
}
